import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';

/**
 * The ADD picker of a test-popup tab (G5's popup list views — Parameter_ListView_Test,
 * the QC Type / Method lookups, Testparameter_ListView_Testmethod_IsGroup): rows from
 * `url`, multi-select with search, OK hands the ticked rows back. With nothing ticked
 * OK shows `emptyMessage` (G5's wording) and stays open.
 */
export function PickListDialog<T extends Record<string, unknown>>({
  title, url, columns, getId, emptyMessage = 'Please select an item to proceed further.', onClose, onPicked, width = 760,
}: {
  title: string;
  url: string;
  columns: AppColumn<T>[];
  getId: (row: T) => string;
  emptyMessage?: string;
  onClose: () => void;
  onPicked: (rows: T[]) => void;
  width?: number;
}) {
  const notify = useNotify();
  const [rows, setRows] = useState<T[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    api.get<{ data?: T[] }>(url)
      .then(({ data }) => { if (alive) setRows(data.data ?? []); })
      .catch(() => { if (alive) notify.error('The list could not be loaded.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url, notify]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => columns.some((c) => String(r[c.field] ?? '').toLowerCase().includes(q)));
  }, [rows, search, columns]);

  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const ok = () => {
    if (selected.size === 0) { notify.info(emptyMessage); return; }
    onPicked(rows.filter((r) => selected.has(getId(r))));
  };

  return (
    <AppDialog
      open
      title={title}
      width={width}
      className="dlg-center tmd-24"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={ok}>OK</button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div className="clp-pick-top">
        <span className="clp-pick-count">{selected.size} selected</span>
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
      </div>
      <AppDataGrid<T>
        rows={visible}
        columns={columns}
        getRowId={(r) => getId(r)}
        loading={loading}
        defaultPageSize={20}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowClick={(r) => toggle(getId(r))}
        emptyText="No data to display"
      />
    </AppDialog>
  );
}
